/**
 * InvitationsPanel — tenant detail panel's Invitations section (WSRPDM-0018,
 * contract §2–4). Defaults to `status=PENDING`, with an "all" toggle. Per
 * PENDING row: Resend (rotates + re-sends, refreshes `expiresAt`) and Revoke
 * (confirm → row becomes `REVOKED`). Non-pending rows are read-only.
 *
 * `status` is rendered exactly as returned — it's server-COMPUTED, never
 * recomputed from timestamps here. A 409 `INVITATION_ALREADY_ACCEPTED`
 * (accepted between list-load and action) and a 404 (not found — treated
 * generically, never leaking detail) both just refetch the list.
 *
 * `refreshKey` lets the sibling MembersPanel trigger a refetch here after
 * `addMember` returns an invitation (202) — wired by `TenantDetailPage.tsx`.
 */
import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useCopy } from '@/hooks/useCopy';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { AppSwitch } from '@/components/ui';
import { listInvitations, resendInvitation, revokeInvitation } from '@/api/saas/tenants';
import { ApiError } from '@/api/errors';
import { isStaffRole, type TenantInvitationDto } from '@/api/saas/types';
import { ConfirmDialog } from './ConfirmDialog';

interface InvitationsPanelProps {
  tenantId: number;
  /** Bump this from the parent to force a refetch (e.g. after MembersPanel invites someone). */
  refreshKey?: number;
}

export function InvitationsPanel({ tenantId, refreshKey = 0 }: InvitationsPanelProps) {
  const { t } = useCopy();
  const [invitations, setInvitations] = useState<TenantInvitationDto[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);
  const [actionError, setActionError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<TenantInvitationDto | null>(null);

  useEffect(() => {
    let cancelled = false;
    listInvitations(tenantId, showAll ? undefined : 'PENDING')
      .then((res) => {
        if (cancelled) return;
        setInvitations(res.filter((inv) => isStaffRole(inv.role)));
        setLoadFailed(false);
      })
      .catch(() => {
        if (cancelled) return;
        setLoadFailed(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [tenantId, showAll, reloadToken, refreshKey]);

  const refetch = () => {
    setLoading(true);
    setReloadToken((n) => n + 1);
  };

  /**
   * Shared mapping for resend/revoke failures: a 409 `INVITATION_ALREADY_ACCEPTED`
   * (accepted between list-load and action) or a 404 (not found — treated
   * generically, never leaking detail) both refetch the list so the row
   * reflects reality; any other failure just shows the generic message.
   */
  const describeActionError = (
    err: unknown,
    genericFallback: string,
  ): { message: string; refetchAfter: boolean } => {
    const apiErr = err instanceof ApiError ? err : null;
    if (apiErr?.code === 'INVITATION_ALREADY_ACCEPTED') {
      return {
        message:
          apiErr.details.message ??
          t(
            'tenantDetail.invitations.alreadyAccepted',
            'This invitation was already accepted. The list has been refreshed.',
          ),
        refetchAfter: true,
      };
    }
    if (apiErr?.status === 404) {
      return {
        message: t(
          'tenantDetail.invitations.notFound',
          'This invitation could not be found. The list has been refreshed.',
        ),
        refetchAfter: true,
      };
    }
    return { message: apiErr?.details.message ?? genericFallback, refetchAfter: false };
  };

  const handleResend = async (invitation: TenantInvitationDto) => {
    setActionError(null);
    setBusyId(invitation.id);
    try {
      await resendInvitation(tenantId, invitation.id);
      refetch();
    } catch (err) {
      const { message, refetchAfter } = describeActionError(
        err,
        t('tenantDetail.invitations.resendError', 'Could not resend invitation. Please try again.'),
      );
      setActionError(message);
      if (refetchAfter) refetch();
    } finally {
      setBusyId(null);
    }
  };

  const handleConfirmRevoke = async () => {
    const invitation = revokeTarget;
    if (!invitation) return;
    setRevokeTarget(null);
    setActionError(null);
    setBusyId(invitation.id);
    try {
      await revokeInvitation(tenantId, invitation.id);
      refetch();
    } catch (err) {
      const { message, refetchAfter } = describeActionError(
        err,
        t('tenantDetail.invitations.revokeError', 'Could not revoke invitation. Please try again.'),
      );
      setActionError(message);
      if (refetchAfter) refetch();
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Box sx={{ mt: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          {t('tenantDetail.invitations.title', 'Invitations')}
        </Typography>
        <AppSwitch
          label={t('tenantDetail.invitations.statusAll', 'Show all')}
          checked={showAll}
          onChange={setShowAll}
        />
      </Box>

      {actionError ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setActionError(null)}>
          {actionError}
        </Alert>
      ) : null}

      {loading ? <LoadingSpinner /> : null}
      {!loading && loadFailed ? (
        <Alert severity="error">
          {t('tenantDetail.invitations.loadError', 'Could not load invitations.')}
        </Alert>
      ) : null}

      {!loading && !loadFailed && invitations ? (
        <TableContainer>
          <Table
            aria-label={t('tenantDetail.invitations.tableAriaLabel', 'Tenant invitations')}
            size="small"
          >
            <TableHead>
              <TableRow>
                <TableCell>{t('tenantDetail.invitations.col.email', 'Email')}</TableCell>
                <TableCell>{t('tenantDetail.invitations.col.role', 'Role')}</TableCell>
                <TableCell>{t('tenantDetail.invitations.col.status', 'Status')}</TableCell>
                <TableCell>{t('tenantDetail.invitations.col.created', 'Created')}</TableCell>
                <TableCell>{t('tenantDetail.invitations.col.expires', 'Expires')}</TableCell>
                <TableCell align="right">
                  {t('tenantDetail.invitations.col.actions', 'Actions')}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {invitations.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                    <Typography variant="body2" color="text.secondary">
                      {showAll
                        ? t('tenantDetail.invitations.emptyAll', 'No invitations.')
                        : t('tenantDetail.invitations.empty', 'No pending invitations.')}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                invitations.map((invitation) => {
                  const busy = busyId === invitation.id;
                  const pending = invitation.status === 'PENDING';
                  return (
                    <TableRow key={invitation.id}>
                      <TableCell>{invitation.email}</TableCell>
                      <TableCell>
                        {t(`tenantDetail.members.role.${invitation.role}`, invitation.role)}
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={t(
                            `tenantDetail.invitations.status.${invitation.status}`,
                            invitation.status,
                          )}
                        />
                      </TableCell>
                      <TableCell>{new Date(invitation.createdAt).toLocaleString()}</TableCell>
                      <TableCell>{new Date(invitation.expiresAt).toLocaleString()}</TableCell>
                      <TableCell align="right">
                        {pending ? (
                          <>
                            <Button
                              size="small"
                              disabled={busy}
                              onClick={() => void handleResend(invitation)}
                            >
                              {t('tenantDetail.invitations.resendButton', 'Resend')}
                            </Button>
                            <Button
                              size="small"
                              color="error"
                              disabled={busy}
                              onClick={() => setRevokeTarget(invitation)}
                            >
                              {t('tenantDetail.invitations.revokeButton', 'Revoke')}
                            </Button>
                          </>
                        ) : null}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      ) : null}

      <ConfirmDialog
        open={revokeTarget !== null}
        title={t('tenantDetail.invitations.revokeConfirmTitle', 'Revoke invitation?')}
        body={t(
          'tenantDetail.invitations.revokeConfirmBody',
          'This revokes the pending invitation to {{email}}. They will no longer be able to accept it.',
          { email: revokeTarget?.email ?? '' },
        )}
        confirmLabel={t('tenantDetail.invitations.revokeButton', 'Revoke')}
        onConfirm={() => void handleConfirmRevoke()}
        onCancel={() => setRevokeTarget(null)}
      />
    </Box>
  );
}
